import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { X, CheckCircle2, Loader2, Phone } from "lucide-react";
import { CONTACT } from "./Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPICS = ["Tax preparation", "Business taxes", "Bookkeeping", "Tax consultation", "Not sure yet"];
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const TIMES = ["Morning (9am – 12pm)", "Midday (12pm – 2pm)", "Afternoon (2pm – 5pm)", "Evening (5pm – 7pm)"];

const inputCls =
  "w-full bg-[#F7F8FA] border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm text-[#1F2933] placeholder-[#5F6B7A] focus:outline-none focus:ring-2 focus:ring-[#C9A227]/70 focus:border-[#C9A227] transition";

export default function AppointmentModal({ open, onClose }) {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", topic: "Tax preparation", preferred_day: "Monday", preferred_time: "Morning (9am – 12pm)", notes: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const submit = async () => {
    const e = {};
    if (form.full_name.trim().length < 2) e.full_name = "Please enter your full name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email address";
    if (form.phone.replace(/\D/g, "").length < 7) e.phone = "Please enter a valid phone number";
    setErrors(e);
    if (Object.keys(e).length) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/appointments`, form);
      setResult(res.data);
    } catch (err) {
      setErrors({ submit: err.response?.data?.detail || "Something went wrong. Please try again or email us at marizematax@gmail.com." });
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setResult(null); setErrors({});
    setForm({ full_name: "", email: "", phone: "", topic: "Tax preparation", preferred_day: "Monday", preferred_time: "Morning (9am – 12pm)", notes: "" });
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        data-testid="appointment-modal"
        className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-md p-0 sm:p-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="bg-white border border-slate-200 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-xl max-h-[92vh] overflow-y-auto p-6 sm:p-8"
          initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between mb-5">
            <div>
              <p className="text-xs uppercase tracking-widest font-semibold text-[#86680A]">Marizema Tax Service LLC</p>
              <h3 className="font-serif-display text-xl sm:text-2xl font-semibold text-[#0B2545] mt-1">Book an Appointment</h3>
              <a data-testid="appointment-phone-link" href={CONTACT.phoneTel}
                className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-[#86680A] transition-colors">
                <Phone size={13} className="text-[#86680A]" /> Prefer to talk? Call {CONTACT.phoneDisplay}
              </a>
            </div>
            <button data-testid="appointment-btn-close" onClick={onClose} className="text-slate-500 hover:text-[#0B2545] p-1" aria-label="Close">
              <X size={22} />
            </button>
          </div>

          {result ? (
            <div data-testid="appointment-success" className="text-center py-6">
              <CheckCircle2 size={56} className="text-emerald-400 mx-auto mb-4" />
              <h4 className="font-serif-display text-xl font-semibold text-[#0B2545] mb-2">Request received</h4>
              <p className="text-sm text-[#1F2933] mb-1">We'll confirm by email within 1 business day.</p>
              <p className="text-sm text-slate-500">
                Reference: <span data-testid="appointment-reference" className="text-[#86680A] font-mono font-semibold">{result.reference}</span>
              </p>
              <button data-testid="appointment-btn-done" onClick={() => { reset(); onClose(); }}
                className="mt-6 px-6 py-2.5 rounded-full bg-[#C9A227] text-[#0B2545] text-sm font-semibold hover:bg-[#D4AF37] transition-colors">
                Done
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[#1F2933] mb-1.5">Full name *</label>
                <input data-testid="appointment-input-name" className={inputCls} placeholder="e.g. Marshet Mekonnen"
                  value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
                {errors.full_name && <p data-testid="appointment-error-name" className="text-xs text-red-600 mt-1">{errors.full_name}</p>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-[#1F2933] mb-1.5">Email *</label>
                  <input data-testid="appointment-input-email" type="email" className={inputCls} placeholder="you@example.com"
                    value={form.email} onChange={(e) => set("email", e.target.value)} />
                  {errors.email && <p data-testid="appointment-error-email" className="text-xs text-red-600 mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#1F2933] mb-1.5">Phone *</label>
                  <input data-testid="appointment-input-phone" type="tel" className={inputCls} placeholder="(813) 555-0000"
                    value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                  {errors.phone && <p data-testid="appointment-error-phone" className="text-xs text-red-600 mt-1">{errors.phone}</p>}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#1F2933] mb-1.5">What would you like to talk about? *</label>
                <select data-testid="appointment-select-topic" className={inputCls} value={form.topic} onChange={(e) => set("topic", e.target.value)}>
                  {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-[#1F2933] mb-1.5">Preferred day (Mon–Sat) *</label>
                  <select data-testid="appointment-select-day" className={inputCls} value={form.preferred_day} onChange={(e) => set("preferred_day", e.target.value)}>
                    {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#1F2933] mb-1.5">Preferred time window *</label>
                  <select data-testid="appointment-select-timewindow" className={inputCls} value={form.preferred_time} onChange={(e) => set("preferred_time", e.target.value)}>
                    {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#1F2933] mb-1.5">Notes (optional)</label>
                <textarea data-testid="appointment-input-notes" rows={3} className={inputCls} placeholder="Anything that helps us prepare…"
                  value={form.notes} onChange={(e) => set("notes", e.target.value)} />
              </div>
              {errors.submit && <p data-testid="appointment-error-submit" className="text-xs text-red-600">{errors.submit}</p>}
              <button data-testid="appointment-btn-submit" onClick={submit} disabled={submitting}
                className="w-full flex items-center justify-center gap-2 text-sm font-semibold px-5 py-3 rounded-full bg-[#C9A227] text-[#0B2545] hover:bg-[#D4AF37] disabled:opacity-60 transition-colors">
                {submitting && <Loader2 size={16} className="animate-spin" />}
                {submitting ? "Sending…" : "Request Appointment"}
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
